import React, { createContext, useState } from 'react';
import { any, string, object } from 'prop-types';
import { connect } from 'react-redux';
import { getTable } from './selectors';

import { usePrevious } from '../../hooks/usePrevious';
import { getFilterQueryParams, getSortQueryParams } from './helpers';

export const TableServerSideContext = createContext({});

/*
 * A required wrapper for TableServerSide
 * Responsible for making React Table values and state accessible to all its children.
 */
export const TableServerSideProvider = ({
  children,
  tableId, // Required for storing column sort/filter values in Redux (to maintain state when navigating between pages).
  savedTableState,
  columnFilterModifiers,
  columnSortModifiers
}) => {
  const [tableInstance, setTableInstance] = useState({});
  const [totalResources, setTotalResources] = useState(0);
  const [refreshData, setRefreshData] = useState(false);

  const refreshTableData = () => {
    setRefreshData(currentValue => !currentValue);
  };

  const { setAllFilters, setSortBy, isAllRowsSelected } = tableInstance;

  const previousTableId = usePrevious(tableId);

  // Ensure that previous filter/sorts do not get applied to the current filter/sort
  const sortBy = previousTableId !== tableId ? {} : tableInstance.sortBy;
  const filters = previousTableId !== tableId ? [] : tableInstance.filters;

  const isFilteringOrSorting = filters?.length > 0 || (sortBy && Object.keys(sortBy)?.length > 0);

  const sortQueryParams = getSortQueryParams(sortBy, columnSortModifiers);
  const filterQueryParams = getFilterQueryParams(filters, columnFilterModifiers);

  // Clears the column sort/filters, while preserving the default sort
  const handleClearSortAndFilters = () => {
    const defaultSortKeys = Object.keys(columnSortModifiers.defaultSort || {});
    const isDefaultSortApplied =
      sortBy && defaultSortKeys.every(defaultSortKey => sortBy[defaultSortKey] !== undefined);

    setSortBy(isDefaultSortApplied ? columnSortModifiers.defaultSort : {});
    setAllFilters([]);
  };

  return (
    <TableServerSideContext.Provider
      value={{
        tableInstance: { ...tableInstance, sortBy, filters },
        setTableInstance,
        setAllFilters,
        setSortBy,
        tableId,
        savedTableState,
        columnFilterModifiers,
        columnSortModifiers,
        sortQueryParams,
        filterQueryParams,
        handleClearSortAndFilters,
        isFilteringOrSorting,
        totalResources,
        setTotalResources,
        isAllRowsSelected,
        refreshData,
        refreshTableData
      }}
    >
      {children}
    </TableServerSideContext.Provider>
  );
};

TableServerSideProvider.propTypes = {
  children: any.isRequired,
  columnFilterModifiers: object.isRequired,
  columnSortModifiers: object.isRequired,
  savedTableState: object.isRequired,
  tableId: string
};

TableServerSideProvider.defaultProps = {
  tableId: ''
};

const mapStateToProps = (state, ownProps) => {
  const tableId = ownProps?.tableId;

  return {
    savedTableState: getTable(state, tableId)
  };
};

export default connect(mapStateToProps)(TableServerSideProvider);
